import { CanvasTextureSlot } from '@/infrastructure/engine3d/textures/CanvasTextureSlot';
import type { Material, Mesh } from 'three';
import type { TCanvasSnapshot } from '@/application/ports/TCanvasSnapshot';
import type { TSide } from '@/domain/shared/TSide';

// Declares "when a snapshot for `side` arrives, paint this rectangle of
// the fabric canvas onto this face of this mesh". A strategy emits an
// array of these at attach() time and the applier runs them on every
// tick — the "how" is generic, the "where/what" is data.
type TTextureSlotDescriptor = {
    // Fill the slot's backing canvas with white before drawImage.
    backgroundFill?: 'white';

    materialIndex: number;
    mesh: Mesh;
    // Optional per-tick side-effect run after the texture is bound.
    // Escape hatch for material properties that depend on the snapshot
    // itself (e.g. Gobelet's transparent/depthWrite from
    // hasOpaqueBackground). Keep it a callback rather than a data flag
    // to avoid the "options object grows into a config language" trap.
    onMaterialAssigned?: (material: Material, snapshot: TCanvasSnapshot) => void;
    side: 'any' | TSide;
    // Override CanvasTextureSlot defaults per descriptor.
    slotOptions?: { anisotropy?: number; flipY?: boolean; mipmaps?: boolean };
    // Sub-region of the snapshot to sample, expressed as ratios in
    // [0, 1] — resolved to pixels at paint time using the snapshot's
    // dimensions. Defaults to the full canvas. Kept as ratios so the
    // descriptor is invariant across snapshot size changes.
    sourceCrop?: { height: number; width: number; x: number; y: number };
    // Applied to the underlying Three texture — used by the card verso
    // mirror ([-1,1] repeat + [1,0] offset).
    uvTransform?: { offset?: [number, number]; repeat?: [number, number] };
};

// Generic canvas → mesh texture applier. Instantiated by each strategy
// with a list of TTextureSlotDescriptor — the strategy owns the
// mapping (which face gets which crop), the applier owns the transport
// (allocation, caching, needsUpdate). Slot cache keyed by descriptor
// object identity: the strategy must reuse the same array across
// apply() calls (natural since descriptors are built once at attach()).
class DescriptorTextureApplier {
    private _descriptors: TTextureSlotDescriptor[] = [];
    private readonly _slots = new Map<TTextureSlotDescriptor, CanvasTextureSlot>();

    configure(descriptors: TTextureSlotDescriptor[]): void {
        this.dispose();
        this._descriptors = descriptors;
    }

    apply(side: TSide, snapshot: TCanvasSnapshot): void {
        this._descriptors.forEach((descriptor) => {
            if (descriptor.side !== 'any' && descriptor.side !== side) {
                return;
            }
            const rect = _resolveRect(descriptor.sourceCrop, snapshot.canvas.width, snapshot.canvas.height);
            const slot = this._getOrCreateSlot(descriptor, rect.width, rect.height);
            slot.paint(snapshot.canvas, { backgroundFill: descriptor.backgroundFill, sourceRect: rect });
            if (descriptor.uvTransform?.repeat) {
                slot.texture.repeat.set(descriptor.uvTransform.repeat[0], descriptor.uvTransform.repeat[1]);
            }
            if (descriptor.uvTransform?.offset) {
                slot.texture.offset.set(descriptor.uvTransform.offset[0], descriptor.uvTransform.offset[1]);
            }

            const materials = Array.isArray(descriptor.mesh.material) ? descriptor.mesh.material : [descriptor.mesh.material];
            const material = materials[descriptor.materialIndex];
            if (!material) {
                return;
            }
            const matWithMap = material as Material & { map?: { dispose(): void } };
            if (matWithMap.map !== slot.texture) {
                // Dispose the previous map if it was a placeholder set at
                // material construction — the builder gives materials an
                // initial white CanvasTexture so the shader compiles with
                // USE_MAP=1 from frame one; we own it and must free the
                // 1×1 GPU texture when we swap in the real snapshot.
                matWithMap.map?.dispose();
                matWithMap.map = slot.texture;
                material.needsUpdate = true;
            }
            descriptor.onMaterialAssigned?.(material, snapshot);
        });
    }

    dispose(): void {
        this._slots.forEach((slot) => slot.dispose());
        this._slots.clear();
        this._descriptors = [];
    }

    private _getOrCreateSlot(descriptor: TTextureSlotDescriptor, width: number, height: number): CanvasTextureSlot {
        const existing = this._slots.get(descriptor);
        if (existing && existing.width === width && existing.height === height) {
            return existing;
        }
        // Snapshot dims changed — recreate (resizing a canvas clears it).
        if (existing) {
            existing.dispose();
        }
        const slot = new CanvasTextureSlot({ height, width, ...descriptor.slotOptions });
        this._slots.set(descriptor, slot);
        return slot;
    }
}

function _resolveRect(
    crop: { height: number; width: number; x: number; y: number } | undefined,
    canvasWidth: number,
    canvasHeight: number,
): { height: number; width: number; x: number; y: number } {
    if (!crop) {
        return { height: canvasHeight, width: canvasWidth, x: 0, y: 0 };
    }
    return {
        height: Math.round(crop.height * canvasHeight),
        width: Math.round(crop.width * canvasWidth),
        x: Math.round(crop.x * canvasWidth),
        y: Math.round(crop.y * canvasHeight),
    };
}

export { DescriptorTextureApplier };
export type { TTextureSlotDescriptor };
